import { createFileRoute } from '@tanstack/react-router'
import { type ColDef } from 'ag-grid-community'
import { Trash } from 'lucide-react'
import { useState, useMemo } from 'react'
import { toast } from 'sonner'

import { ValidateProductImportError, ValidateProductImportResponse } from '@/api/admin-products-types'
import { useImportProducts, useValidateProductImport } from '@/api/api-mutations'
import { Button } from '@/components/base/button'
import { Divider } from '@/components/base/divider'
import { Drawer } from '@/components/base/drawer'
import { FilePreview } from '@/components/base/file-preview'
import { FileType, FileUpload } from '@/components/base/file-upload'
import { Heading } from '@/components/base/heading'
import { Hint } from '@/components/base/hint'
import { IconButton } from '@/components/base/icon-button'
import { Text } from '@/components/base/text'
import { RouteModal, useRouteModal } from '@/components/route-modal'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { Spinner } from '@/icons/spinner'

export const Route = createFileRoute('/_authenticated/_general/products/import')({
	component: ProductImport
})

function ProductImport() {
	return (
		<RouteModal asDrawer prev='..'>
			<ProductImportComponent />
		</RouteModal>
	)
}

function ProductImportComponent() {
	const { L } = useI18n()
	const [file, setFile] = useState<File | undefined>()
	const [fileName, setFileName] = useState<string | undefined>()

	const importProducts = useImportProducts(true)
	const validateImport = useValidateProductImport(true)
	const { handleSuccess } = useRouteModal()

	const productImportTemplateContent = useMemo(() => {
		return getProductImportCsvTemplate()
	}, [])

	const handleUploaded = async (file: File) => {
		setFile(file)
		setFileName(file.name)

		const form = new FormData()
		form.append('file', file)
		await validateImport.mutateAsync(
			{ query: {}, body: form },
			{
				onError: err => {
					toast.error(err.message)
					setFile(undefined)
					setFileName(undefined)
				}
			}
		)
	}

	const handleConfirm = async () => {
		if (!file) {
			return
		}

		const form = new FormData()
		form.append('file', file)
		await importProducts.mutateAsync(
			{ query: {}, body: form },
			{
				onError: err => {
					setFile(undefined)
					setFileName(undefined)
					toast.error(err.message)
				}
			}
		)
	}

	const validationResult = validateImport.data?.isSuccess ? validateImport.data?.response : undefined
	const canImport =
		!!fileName &&
		!validateImport.isPending &&
		validateImport.data?.isSuccess === true &&
		(validationResult?.errors.length ?? 0) === 0 &&
		!importProducts.isPending

	return (
		<>
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>
						{importProducts.data?.isSuccess ? L.Products.Import.Success() : L.Products.Import.Header()}
					</Heading>
				</Drawer.Title>
				<Drawer.Description className='sr-only'>{L.Products.Import.Description()}</Drawer.Description>
			</Drawer.Header>
			<Drawer.Body>
				{importProducts.data?.isSuccess ? (
					<>
						<ImportSummary validation={undefined} importResult={importProducts.data?.response} />
					</>
				) : (
					<>
						<Heading level='h2'>{L.Products.Import.Upload.Title()}</Heading>
						<Text size='small' className='text-ui-fg-subtle'>
							{L.Products.Import.Upload.Description()}
						</Text>

						<div className='mt-4'>
							{fileName ? (
								<FilePreview
									filename={fileName}
									loading={validateImport.isPending}
									activity={L.Products.Import.Upload.Preprocessing()}
									button={
										<IconButton
											variant='transparent'
											className='cursor-pointer'
											asChild
											onClick={() => {
												setFile(undefined)
												setFileName(undefined)
											}}
										>
											<Trash />
										</IconButton>
									}
								/>
							) : (
								<UploadImport onUploaded={handleUploaded} />
							)}
						</div>

						{validateImport.data?.isSuccess && !!fileName && (
							<div className='mt-4'>
								<ImportSummary
									validation={!importProducts.data ? validationResult : undefined}
									isImporting={importProducts.isPending}
								/>
							</div>
						)}

						<Heading className='mt-6' level='h2'>
							{L.Products.Import.Template.Title()}
						</Heading>
						<Text size='small' className='text-ui-fg-subtle'>
							{L.Products.Import.Template.Description()}
						</Text>
						<div className='mt-4'>
							<FilePreview filename='product-import-template.csv' url={productImportTemplateContent} />
						</div>
					</>
				)}
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center gap-x-2'>
					<Drawer.Close asChild>
						<Button size='small' variant='secondary'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<Button
						onClick={importProducts.data?.isSuccess ? () => handleSuccess() : handleConfirm}
						size='small'
						disabled={importProducts.data?.isSuccess ? false : !canImport}
						isLoading={importProducts.isPending}
					>
						{importProducts.data?.isSuccess ? L.General.Close() : L.General.Import()}
					</Button>
				</div>
			</Drawer.Footer>
		</>
	)
}

const ProductImportCSV =
	'data:text/csv;charset=utf-8,' +
	`Title,Description,Status,Category Title,Groups,External Id,SKU,Barcode,Pricing Method,Price: <Sales Channel Name>,Tax code: <Sales Channel Name>,Tax inclusive: <Sales Channel Name>,Sales channel group price: <Sales Channel Group Name>,Sales channel group tax code: <Sales Channel Group Name>,Sales channel group tax inclusive: <Sales Channel Group Name>,Image Url
"Example Product","Product description","PUBLISHED","Category.SubCategory","Group1,Group2","EXT123","SKU123","123456789,987654321","PER_UNIT","10.99","VAT25","TRUE","9.99","VAT25","TRUE","https://example.com/image.jpg"
`

export const getProductImportCsvTemplate = () => {
	return encodeURI(ProductImportCSV)
}

type ImportResult = {
	created: number
	updated: number
	errored: number
	errors: ValidateProductImportError[]
}

export const ImportSummary = ({
	validation,
	isImporting,
	importResult
}: {
	validation?: ValidateProductImportResponse
	isImporting?: boolean
	importResult?: ImportResult
}) => {
	const { L } = useI18n()

	if (isImporting) {
		return (
			<div className='flex flex-col items-center justify-center gap-4 py-8'>
				<Spinner className='animate-spin text-ui-fg-subtle' />
				<Text size='base' className='text-ui-fg-subtle'>
					{L.Products.Import.Upload.Importing()}
				</Text>
			</div>
		)
	}

	if (importResult) {
		return (
			<div className='flex flex-col gap-4'>
				<div className='flex flex-row rounded-md bg-ui-bg-component px-3 py-2 shadow-elevation-card-rest transition-fg'>
					<Stat
						title={importResult.created.toLocaleString()}
						description={L.Products.Import.Upload.ProductsCreated()}
					/>
					<Divider orientation='vertical' className='h-10 px-3' />
					<Stat
						title={importResult.updated.toLocaleString()}
						description={L.Products.Import.Upload.ProductsUpdated()}
					/>
					<Divider orientation='vertical' className='h-10 px-3' />
					<Stat
						title={importResult.errored.toLocaleString()}
						description={L.Products.Import.Upload.ProductsErrored()}
					/>
				</div>
				{importResult.errors.length > 0 && (
					<ErrorTable errors={importResult.errors} errored={importResult.errored} />
				)}
			</div>
		)
	}

	if (!validation) return null

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex flex-col gap-2'>
				<Text size='base' className='text-ui-fg-base'>
					{L.Products.Import.Header()}
				</Text>
				<div className='flex flex-row rounded-md bg-ui-bg-component px-3 py-2 shadow-elevation-card-rest transition-fg'>
					<Stat
						title={validation.toCreate.toLocaleString()}
						description={L.Products.Import.Upload.ProductsToCreate()}
					/>
					<Divider orientation='vertical' className='h-10 px-3' />
					<Stat
						title={validation.toUpdate.toLocaleString()}
						description={L.Products.Import.Upload.ProductsToUpdate()}
					/>
				</div>
			</div>

			{validation.errors.length > 0 && <ErrorTable errors={validation.errors} errored={validation.errored} />}
		</div>
	)
}

const Stat = ({ title, description }: { title: string; description: string }) => {
	return (
		<div className='flex flex-1 flex-col justify-center'>
			<Text size='xlarge' className='font-sans font-medium'>
				{title}
			</Text>

			<Text leading='compact' size='xsmall' weight='plus' className='text-ui-fg-subtle'>
				{description}
			</Text>
		</div>
	)
}

const ErrorTable = ({ errors, errored }: { errors: ValidateProductImportError[]; errored: number }) => {
	const { L } = useI18n()

	const columnDefs = useMemo<ColDef<ValidateProductImportError>[]>(
		() => [
			{
				headerName: L.Products.Import.Upload.Errors.Row(),
				field: 'row'
			},
			{
				headerName: L.Products.Import.Upload.Errors.Column(),
				field: 'column'
			},
			{
				headerName: L.Products.Import.Upload.Errors.Message(),
				field: 'message',
				flex: 1
			}
		],
		[L]
	)

	return (
		<>
			<div className='flex flex-col gap-2 rounded-md bg-ui-bg-component p-3 shadow-elevation-card-rest transition-fg'>
				<div className='flex flex-col gap-2'>
					<Text size='xlarge' className='text-ui-fg-base'>
						{L.Products.Import.Upload.Errors.Title()}
					</Text>
					<Text leading='compact' size='xsmall' weight='plus' className='text-ui-fg-subtle'>
						{L.Products.Import.Upload.Errors.Description({
							count: errors.length,
							rows: errored
						})}
					</Text>
				</div>
				<div className='h-[250px] overflow-y-auto bg-ui-bg-component'>
					<Table<ValidateProductImportError> data={errors} columnDefs={columnDefs} />
				</div>
			</div>
		</>
	)
}

const SUPPORTED_FORMATS = ['text/csv']
const SUPPORTED_FORMATS_FILE_EXTENSIONS = ['.csv']

export const UploadImport = ({
	onUploaded,
	supportedFormats,
	supportedExtensions
}: {
	onUploaded: (file: File) => void
	supportedFormats?: string[]
	supportedExtensions?: string[]
}) => {
	const { L } = useI18n()
	const [error, setError] = useState<string>()

	const hasInvalidFiles = (fileList: FileType[]) => {
		const invalidFile = supportedFormats ? fileList.find(f => !supportedFormats?.includes(f.file.type)) : undefined

		if (invalidFile) {
			setError(L.General.InvalidFileType(invalidFile.file.name, supportedExtensions?.join(', ') ?? ''))

			return true
		}

		return false
	}

	return (
		<div className='flex flex-col gap-y-4'>
			<FileUpload
				label={L.Products.Import.UploadLabel()}
				hint={L.Products.Import.UploadHint()}
				multiple={false}
				hasError={!!error}
				formats={SUPPORTED_FORMATS}
				onUploaded={files => {
					setError(undefined)
					if (hasInvalidFiles(files)) {
						return
					}
					onUploaded(files[0].file)
				}}
			/>

			{error && (
				<div>
					<Hint variant='error'>{error}</Hint>
				</div>
			)}
		</div>
	)
}
